import * as React from "react";

import {
  Box,
  Button,
  Center,
  Container,
  Group,
  ScrollArea,
  Stack,
  Switch,
  Text,
  Title,
} from "@mantine/core";
import { StyleSheet } from "@react-pdf/renderer";
import { IconChecklist, IconDownload } from "@tabler/icons-react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";

import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useCountEvent } from "../../hooks/useCountEvent";
import { useDownloadPDF } from "../../hooks/useDownloadPDF";
import { AircraftAddonTemplate } from "../../lib/model/addonTemplate";
import { ChecklistContents } from "../../lib/model/checklistAppState";
import {
  SerializedChecklistContents,
  SerializedChecklistDocument,
} from "../../lib/model/checklistSerialized";
import { aircraftAddonTemplates } from "../../lib/state/addonTemplates";
import { appStateFromSerialized } from "../../lib/state/convert";
import { defaultDocumentStyles } from "../../lib/state/defaultStyles";
import { librarySlice } from "../../lib/state/reducer";
import { HardPDFPreview } from "../renderer/pdf/PDFPreview";
import { SoftPDFPreview } from "../renderer/softpdf/SoftPDFPreview";

export const TemplateInfoPage = ({ type }: { type: "aircraft" | "addon" }): JSX.Element | null => {
  const params = useParams();
  const navigate = useNavigate();

  const addonId = params.addonId;

  React.useEffect(() => {
    if (!addonId || !aircraftAddonTemplates.map((x) => x.id).includes(addonId)) {
      navigate("/");
    }
  }, [addonId, aircraftAddonTemplates]);

  if (!addonId) {
    return null;
  }

  switch (type) {
    case "aircraft": {
      const addon = aircraftAddonTemplates.find((x) => x.id === addonId);
      if (!addon) {
        return <></>;
      }
      return <AircraftTemplateView addon={addon} />;
    }
    case "addon": {
      return <></>;
    }
    default: {
      const _exhaustiveCheck: never = type;
      return _exhaustiveCheck;
    }
  }
};

const AircraftTemplateView = ({ addon }: { addon: AircraftAddonTemplate }): JSX.Element => {
  const [showPdf, setShowPdf] = React.useState<boolean>(false);

  const document: SerializedChecklistDocument = {
    title: addon.title,
    contents: {
      title: addon.title,
      checklists: addon.content,
    },
    styles: defaultDocumentStyles,
    layout: "twoColumn",
    options: {
      autoCapitalize: "none",
      includeSimOnlyWarning: true,
      includeAttributionInFooter: true,
    },
  };

  const previewSerialized: SerializedChecklistContents = {
    title: addon.title,
    checklists: addon.content,
  };
  const previewDocument: ChecklistContents = appStateFromSerialized(previewSerialized);
  const previewStyleSheet = StyleSheet.create(defaultDocumentStyles);

  const { download: downloadPdf, isLoading: isDownloading } = useDownloadPDF();
  const reduxDispatch = useAppDispatch();
  const navigate = useNavigate();
  const countEvent = useCountEvent();

  const InfoMarketingBlurb = addon.infoMarketingBlurb || (() => null);

  return (
    <Container size="md" py="xl" style={{}}>
      <Helmet>
        <title>{addon.title} Checklist Template</title>
      </Helmet>
      <Stack sx={{ height: "100%" }} align="stretch">
        <Stack align="stretch" spacing="md">
          <Center>
            <Title order={1}>{addon.title} Checklist</Title>
          </Center>
          <Box>
            <InfoMarketingBlurb />
          </Box>
        </Stack>
        <Stack align="center">
          <Group align="center">
            <Button
              variant="gradient"
              gradient={{ from: "cyan.4", to: "blue", deg: 45 }}
              leftIcon={<IconChecklist size={20} />}
              onClick={() => {
                countEvent("customize-from-template-info", addon.id);
                reduxDispatch(librarySlice.actions.newDocumentFromSerialized(document));
                navigate("/editor");
              }}
            >
              Customize...
            </Button>
            <Button
              variant="gradient"
              gradient={{ from: "orange", to: "red", deg: 45 }}
              leftIcon={<IconDownload size={20} />}
              onClick={() => {
                countEvent("download-from-template-info", addon.id);
                downloadPdf(document);
              }}
              loading={isDownloading}
            >
              Download PDF
            </Button>
          </Group>
        </Stack>
        <Stack align="center" sx={{ height: "100%" }}>
          <Switch
            checked={showPdf}
            onChange={(e) => setShowPdf(e.currentTarget.checked)}
            label={<Text>Preview Generated PDF</Text>}
          />
          {showPdf ? (
            <Box style={{ height: "11in", width: "8in" }}>
              <HardPDFPreview document={document} />
            </Box>
          ) : (
            <ScrollArea sx={() => ({ width: "100%", height: "11in" })}>
              <SoftPDFPreview
                checklistDocumentSnapshot={previewDocument}
                stylesheetSnapshot={previewStyleSheet}
                layout="twoColumn"
              />
            </ScrollArea>
          )}
        </Stack>
      </Stack>
    </Container>
  );
};
